/**
 * 资产「名称 + 描述」独立编辑弹窗（角色 / 物品 / 场景 共用）
 *
 * 设计：
 *  - 普通 modal，不与详情抽屉耦合
 *  - 单行名称 + 多行描述
 *  - Esc / 取消按钮 / 蒙层点击都可关闭
 *  - Enter（在 input 内）保存
 *  - 保存成功由父组件处理（toast + 刷新），onSave 抛错时保留弹窗供用户重试
 *  - 主色按 accent 切换：sky / amber / cyan，与三类资产配色对齐
 */

import { useEffect, useState } from 'react';
import { Loader2, Save, X, Pencil } from 'lucide-react';

interface Props {
  open: boolean;
  /** 资产类型标题，例如「编辑角色」「编辑物品」「编辑场景」 */
  title: string;
  initialName: string;
  initialDescription: string | null;
  namePlaceholder?: string;
  descriptionPlaceholder?: string;
  nameMax?: number;
  descriptionMax?: number;
  accent?: 'sky' | 'amber' | 'cyan';
  onClose: () => void;
  onSave: (next: { name: string; description: string | null }) => Promise<void>;
}

const ACCENT_RING = {
  sky: 'focus:border-sky-300 focus:ring-sky-100',
  amber: 'focus:border-amber-300 focus:ring-amber-100',
  cyan: 'focus:border-cyan-300 focus:ring-cyan-100',
} as const;

const ACCENT_BTN = {
  sky: 'bg-sky-600 hover:bg-sky-700',
  amber: 'bg-amber-600 hover:bg-amber-700',
  cyan: 'bg-cyan-600 hover:bg-cyan-700',
} as const;

const ACCENT_HERO_BLOB = {
  sky: 'bg-sky-400/20',
  amber: 'bg-amber-400/20',
  cyan: 'bg-cyan-400/20',
} as const;

const ACCENT_HERO_BADGE = {
  sky: 'text-sky-200',
  amber: 'text-amber-200',
  cyan: 'text-cyan-200',
} as const;

export function AssetMetaEditDialog({
  open,
  title,
  initialName,
  initialDescription,
  namePlaceholder = '名称',
  descriptionPlaceholder = '可选描述',
  nameMax = 60,
  descriptionMax = 500,
  accent = 'sky',
  onClose,
  onSave,
}: Props) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription ?? '');
  const [saving, setSaving] = useState(false);

  // 每次打开重置为最新初始值（不同资产重用同一弹窗实例时）
  useEffect(() => {
    if (open) {
      setName(initialName);
      setDescription(initialDescription ?? '');
      setSaving(false);
    }
  }, [open, initialName, initialDescription]);

  // Esc 关闭
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    const nextDescription = description.trim() ? description.trim() : null;
    if (
      trimmedName === initialName &&
      nextDescription === (initialDescription ?? null)
    ) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await onSave({ name: trimmedName, description: nextDescription });
      onClose();
    } catch {
      // 父组件已 toast，保留弹窗让用户重试
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <button
        type="button"
        aria-label="关闭"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <div
        className="relative flex w-full max-w-md flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 顶部 hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white">
          <div
            className={`absolute right-[-40px] top-[-40px] h-32 w-32 rounded-full blur-2xl ${ACCENT_HERO_BLOB[accent]}`}
          />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-200">
                <Pencil className={`h-3 w-3 ${ACCENT_HERO_BADGE[accent]}`} />
                修改信息
              </div>
              <h2 className="text-lg font-semibold leading-tight">{title}</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"
              aria-label="关闭"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-4 bg-slate-50 p-5">
          {/* 名称 */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
              名称 <span className="text-rose-500">*</span>
            </label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, nameMax))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
              }}
              placeholder={namePlaceholder}
              className={`w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:ring-2 ${ACCENT_RING[accent]}`}
            />
          </div>

          {/* 描述 */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                描述
              </label>
              <span className="text-[10px] text-slate-400">
                {description.length}/{descriptionMax}
              </span>
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, descriptionMax))}
              placeholder={descriptionPlaceholder}
              rows={4}
              className={`w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:ring-2 ${ACCENT_RING[accent]}`}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white shadow-md transition disabled:cursor-not-allowed disabled:opacity-50 ${ACCENT_BTN[accent]}`}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            保存
          </button>
        </div>
      </div>
    </div>
  );
}
